import React, { forwardRef, useCallback, useState } from "react";
import { FieldTypes, urlRegexValidator } from "utils/formUtils";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { isEmpty } from "lodash";

import GenericPullCredentialsForm from "../GenericPullCredentialsForm";
import UpwindMappingForm from "./UpwindMappingForm";

// Static asset categories and sub-categories per ENG-84515
// Upwind API does not provide an endpoint to fetch available categories
const ASSET_CATEGORIES = [
  { label: "Compute Platform", value: "compute_platform" },
];

const ASSET_SUB_CATEGORIES = {
  compute_platform: [
    { label: "Virtual Machine", value: "virtual_machine" },
    { label: "Container Cluster", value: "container_cluster" },
    { label: "Kubernetes Workload", value: "kubernetes_workload" },
    { label: "Scaling Group", value: "scaling_group" },
  ],
};

const UpwindCredentialsForm = (props, ref) => {
  const featureFlags = useFeatureFlag();
  const [enableFetchAssets, setEnableFetchAssets] = useState(() => {
    return !isEmpty(props.config?.clientSecret);
  });
  const [fetchAssetsEnabled, setFetchAssetsEnabled] = useState(() => {
    return props.config?.fetchAssets === "true" || props.config?.fetchAssets === true;
  });
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return props.config?.assetCategory || null;
  });

  const fields = [
    {
      name: "apiEndpoint",
      label: "API Endpoint",
      rules: [urlRegexValidator],
      required: true,
    },
    {
      name: "organization",
      label: "Organization Id",
      type: FieldTypes.PASSWORD,
      required: true,
    },
    {
      name: "clientId",
      label: "Client Id",
      type: FieldTypes.PASSWORD,
      required: true,
    },
    {
      name: "clientSecret",
      label: "Client Secret",
      type: FieldTypes.PASSWORD,
      required: true,
      props: {
        onChange: (e) => {
          setEnableFetchAssets(!isEmpty(e.target.value));
        },
      },
    },
    ...(featureFlags.RBVM_ASSETS
      ? [
          {
            name: "fetchAssets",
            label: "Fetch Assets",
            type: FieldTypes.BOOLEAN,
            disabled: !enableFetchAssets,
            inline: true,
            props: {
              onChange: (e) => setFetchAssetsEnabled(e?.target?.checked),
            },
          },
          {
            name: "assetCategory",
            type: FieldTypes.SELECT,
            options: ASSET_CATEGORIES,
            placeholder: "Select Asset Category",
            label: "Asset Category",
            disabled: !enableFetchAssets || !fetchAssetsEnabled,
            required: false,
            props: {
              onChange: (value) => {
                setSelectedCategory(value);
              },
            },
          },
          {
            name: "assetSubCategories",
            type: FieldTypes.MULTI_SELECT,
            options: ASSET_SUB_CATEGORIES[selectedCategory],
            placeholder: "Leave Blank to Select All",
            label: "Asset Sub-Categories",
            disabled: !enableFetchAssets || !fetchAssetsEnabled || !selectedCategory,
            required: false,
          },
        ]
      : []),
  ];

  const beforeSave = useCallback(
    (data) => {
      const { assetCategory, assetSubCategories, fetchAssets = false } = data;
      return {
        ...data,
        assetCategory: isEmpty(assetCategory) ? null : assetCategory,
        assetSubCategories: isEmpty(assetSubCategories) ? null : assetSubCategories.join(),
        fetchAssets: featureFlags.RBVM_ASSETS && fetchAssets,
      };
    },
    [featureFlags.RBVM_ASSETS]
  );

  const parseExtraFields = useCallback((config) => {
    return {
      ...config,
      fetchAssets: config.fetchAssets === "true" || config.fetchAssets === true,
      assetCategory: config.assetCategory || null,
      assetSubCategories: isEmpty(config.assetSubCategories)
        ? null
        : config.assetSubCategories.split(","),
    };
  }, []);

  return (
    <GenericPullCredentialsForm
      {...props}
      ref={ref}
      fields={fields}
      beforeSave={beforeSave}
      parseExtraFields={parseExtraFields}
      MappingForm={UpwindMappingForm}
      initialValue={{ apiEndpoint: "https://api.upwind.io" }}
    />
  );
};

export default forwardRef(UpwindCredentialsForm);
